<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon awesome-plus-circle.png" alt="" width="25" height="25" />
      <p class="title-bk">先生登録</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <router-link :to="{ name: 'teacher' }">戻る</router-link>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <a href="/pdf/form_teacher.pdf" target="_blank">フォームの説明を確認</a>
    </div>
    <div class="container">
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(confirmAdded)" ref="form">
          <table class="table table-form">
            <tbody>
              <ChoiceField
                v-model="corporationChoiceFieldMultiModels"
                extraRules="required_select"
                label="法人名（法人ID）"
                placeholder="選択して下さい"
                resourceType="corporation"
                modelFieldName="corporation"
                :choiceDisable="loggedInAsCorporation || loggedInAsBrand"
                :childFieldNames="['brand']"
                :formData="currentTeacher"
              />
              <ChoiceField
                v-model="brandChoiceFieldMultiModels"
                extraRules="required_select"
                label="ブランド名（ブランドID）"
                placeholder="選択して下さい"
                resourceType="brand"
                modelFieldName="brand"
                :choiceDisable="loggedInAsBrand || isEmpty(currentTeacher.corporation)"
                :formData="currentTeacher"
              />
              <br />
              <IdInputField
                v-model="currentTeacher.uid"
                extraRules="required"
                label="先生ID"
                placeholder="半角英数字で入力してください（-、_は可）"
                inputClass="col-lg-6"
                resourceType="teacher"
              />
              <PasswordField vid="password" v-model="currentTeacher.password" extraRules="required" />
              <PasswordField
                v-model="verifyPassword"
                extraRules="required|verify_password:password"
                label="パスワード（確認）"
              />
              <br />
              <TextField v-model="currentTeacher.name" extraRules="required" label="先生名" />
              <TextField
                v-model="currentTeacher.nameReading"
                extraRules="required"
                label="先生名（フリガナ）"
                placeholder="全角カタカナ"
              />
              <tr>
                <th scope="row" width="25%">生年月</th>
                <td width="10%">
                  <span class="bg-red">必須</span>
                </td>
                <td class="form-inline no-margin-top">
                  <ValidationProvider
                    mode="eager"
                    rules="required_select"
                    class="v-select-birth"
                    v-slot="{ classes, valid, invalid, errors }"
                  >
                    <v-select
                      tag="div"
                      v-model="currentTeacher.yearOfBirth"
                      placeholder="選択してください"
                      class="form-control select-year"
                      :class="classes"
                      :clearable="false"
                      :options="previousYears"
                    />
                    <span class="select-text">年</span>
                    <div class="invalid-feedback" v-show="invalid">{{ errors[0] }}</div>
                  </ValidationProvider>
                  <ValidationProvider
                    mode="eager"
                    rules="required_select"
                    class="v-select-birth"
                    v-slot="{ classes, valid, invalid, errors }"
                  >
                    <v-select
                      tag="div"
                      v-model="currentTeacher.monthOfBirth"
                      placeholder="選択してください"
                      class="form-control select-month"
                      :class="classes"
                      :clearable="false"
                      :options="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]"
                    />
                    <span class="select-text">月</span>
                    <div class="invalid-feedback" v-show="invalid">{{ errors[0] }}</div>
                  </ValidationProvider>
                </td>
              </tr>
              <SelectField
                label="性別"
                extraRules="required_select"
                inputClass="col-lg-3"
                placeholder="選択してください"
                :options="genders"
                optionValue="id"
                optionLabel="name"
                v-model="currentTeacher.genderId"
              />
              <SelectField
                label="国籍"
                extraRules="required_select"
                inputClass="col-lg-3"
                placeholder="選択してください"
                :options="countries"
                optionValue="id"
                optionLabel="companyjp"
                v-model="currentTeacher.nationalityId"
              />
              <SelectField
                label="雇用形態"
                extraRules="required_select"
                inputClass="col-lg-3"
                placeholder="選択してください"
                :options="employmentStatuses"
                optionValue="id"
                optionLabel="name"
                v-model="currentTeacher.employmentStatusId"
              />
            </tbody>
          </table>
          <div class="text-center">
            <button type="submit" class="btn-confirm">確認</button>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import EventBus from '@/helpers/EventBus.js';
import TextField from '@/components/forms/TextField.vue';
import SelectField from '@/components/forms/SelectField.vue';
import PasswordField from '@/components/forms/PasswordField.vue';
import ChoiceField from '@/components/forms/ChoiceField.vue';
import IdInputField from '@/components/forms/IdInputField.vue';
import { basicCorporationNameIdPresent, basicBrandNameIdPresent } from '@/presenters/fields';
import { yearsFrom } from '@/utils/year_helpers';

export default {
  data() {
    return {
      verifyPassword: '',
      currentTeacher: {},
    };
  },
  components: {
    ValidationObserver,
    ValidationProvider,
    TextField,
    SelectField,
    PasswordField,
    ChoiceField,
    IdInputField,
  },
  computed: {
    corporationChoiceFieldMultiModels() {
      return basicCorporationNameIdPresent(this.currentTeacher.corporation);
    },
    brandChoiceFieldMultiModels() {
      return basicBrandNameIdPresent(this.currentTeacher.brand);
    },
    previousYears() {
      return yearsFrom(1940);
    },
    ...mapState(['teacher']),
    ...mapState('form', ['prevFormData']),
    ...mapState(['corporation']),
    ...mapState(['brand']),
    ...mapGetters('auth', ['loggedInAsCorporation', 'loggedInAsBrand']),
    ...mapState({
      countries: (state) => state.country.countries,
      genders: (state) => state.gender.genders,
      employmentStatuses: (state) => state.employment_status.employmentStatuses,
    }),
  },
  mounted() {
    EventBus.$on('SET_USER_PROFILE', this.setCurrentRoleHandler);
  },
  created() {
    this.currentTeacher = this.createFreshTeacherObject();
  },
  beforeDestroy() {
    EventBus.$off('SET_USER_PROFILE', this.setCurrentRoleHandler);
  },
  methods: {
    createFreshTeacherObject() {
      if (this.teacher.editingTeacher) {
        this.$logger.debug('Editing old Teacher with ID ' + this.teacher.editingTeacher);
        return this.teacher.editingTeacher;
      } else if (this.prevFormData) {
        this.$logger.debug('Restoring previous form data', this.prevFormData);
        return this.prevFormData;
      } else {
        let newTeacher = {
          corporation: null,
          brand: null,
          uid: '',
          name: '',
          nameReading: '',
          yearOfBirth: '',
          monthOfBirth: '',
          genderId: '',
          nationalityId: '',
          employmentStatusId: '',
        };
        if (this.loggedInAsCorporation) {
          newTeacher['corporation'] = this.corporation.corporation;
        }
        if (this.loggedInAsBrand) {
          newTeacher['brand'] = this.brand.brand;
          newTeacher['corporation'] = this.brand.brand.corporation;
        }
        return newTeacher;
      }
    },
    confirmAdded() {
      this.$logger.debug('Started Adding: ' + JSON.stringify(this.currentTeacher));
      this.startEditingTeacher(this.currentTeacher);
      this.$router.push({ name: 'teacher-confirm' });
    },
    setCurrentRoleHandler(payload) {
      if (this.loggedInAsCorporation) {
        this.currentTeacher.corporation = payload.data;
      }

      if (this.loggedInAsBrand) {
        this.currentTeacher.brand = payload.data;
        this.currentTeacher.corporation = payload.data.corporation;
      }
    },
    ...mapActions('teacher', ['startEditingTeacher']),
  },
};
</script>
<style lang="scss" scoped>
.btn-confirm {
  margin-left: 0px;
}
.img-inline {
  margin-left: 5px;
}
.line-break {
  width: 100%;
}
.body-td-explain {
  margin-left: 5px;
  font-size: 10px;
}

/deep/ .v-select-birth {
  .select-month,
  .select-year {
    padding: 0px 2px 0px 10px;
    max-width: initial;
    margin-right: 10px;
    .vs__dropdown-toggle {
      width: 146px;
    }
  }
  .select-text {
    margin-right: 35px;
  }
}
</style>
